import React, { Component } from 'react';
import './App.scss';
import { CircularProgress, CssBaseline } from "@material-ui/core";
import MapComponent from "./components/MapComponent";
import InitSessionScreen from "./components/InitSessionScreen";
import GeoPermissionFailCard from "./components/GeoPermissionFailCard";
import logoImage from "./assets/logo_menu.png";
import logoImagePlain from "./assets/app-icon.svg";
import Menu from "./components/Menu";
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import log from 'loglevel';

import SiteAuth from "./common/SiteAuth";
import PermissionsService from "./common/PermissionsService";
import MapService from "./common/MapService";
import AppService from "./common/AppService";
import UpdateService from "./common/UpdateService";
import { geoPermStatus } from "./common/Constants";
import SnackComponent from "./components/SnackComponent";
import ScreenLogCard from "./components/ScreenLogCard";

import AppUrlListener from './common/AppUrlListener';
import UrlService from "./common/UrlService";
import UrlParamsService from "./common/UrlParamsService";
import { CityService } from "./common/CityService";
import AppStorage from "./storage/AppStorage";
import NetworkService from "./common/NetworkService";
import GronzePageService from "./common/GronzePageService";
import GeoService from "./common/GeoService";
import LogManager from "./common/LogManager";
import i18n from "./i18n";
import { ModalComponentService } from "./common/ModalComponentService";
import RateAppService from "./common/RateAppService";
import AppLifecycleService from "./common/AppLifecycleService";
import type { ReactNode, RefObject } from 'react';
import type { Logger } from 'loglevel';
import type { ModalComponentType } from "./common/ModalComponentService";
import type MenuComponent from "./components/Menu";
import type UserData from "./data/UserData";

declare global {
    interface Window {
        /** Variables de entorno que la app expone al ejecutarse. @see App.loadEnv */
        env: { base_url?: string };
    }
}

interface AppState {
    mapVisible: boolean;
    authenticated: boolean | UserData | null;
    geoPermissionsFail: boolean;
    menuVisible: boolean;
    initialized: boolean;
    /**
     * True cuando initApp ya ha decidido que pantalla toca. Hasta entonces no
     * se pinta el login: isLoading no vale para esto porque lo baja cualquier
     * peticion que termine a mitad del arranque (token, usuario actual...).
     */
    startupResolved: boolean;
    gronzePageComponent: ReactNode;
    isLoading: boolean;
    initialToastShown: boolean;
    modalComponentType: ModalComponentType | null;
    /** True si el backend publica una version mayor que la instalada. */
    updateAvailable: boolean;
    /** Lo escribe initPage; no forma parte del estado inicial. */
    geoPermissionsGranted?: boolean;
}

class App extends Component<object, AppState> {
    // Reference to map component.
    mapRef: RefObject<MapComponent>;
    menu: RefObject<MenuComponent>;
    appContainerRef: RefObject<HTMLDivElement>;

    // Geo permissions options.
    lastGeoState: string | undefined;
    geoPermissionsGranted: boolean | undefined;

    constructor(props: object) {
        super(props);

        this.state = {
            mapVisible: false,
            authenticated: false,
            geoPermissionsFail: false,
            menuVisible: false,
            initialized: false,
            startupResolved: false,
            gronzePageComponent: null,
            isLoading: true,
            initialToastShown: GeoService.isUsingConfigInitialLocation(),
            modalComponentType: null,
            updateAvailable: false,
        }

        this.handleScreenFocus = this.handleScreenFocus.bind(this);
        this.changePermissionHandler = this.changePermissionHandler.bind(this);
        this.toggleMenu = this.toggleMenu.bind(this);
        this.closeMenu = this.closeMenu.bind(this);
        this.onUnautorize = this.onUnautorize.bind(this);
        this.onAutorize = this.onAutorize.bind(this);
        this.onMenuOpen = this.onMenuOpen.bind(this);
        this.onMenuClose = this.onMenuClose.bind(this);
        this.handleCompoentChange = this.handleCompoentChange.bind(this);
        this.checkAppUpdate = this.checkAppUpdate.bind(this);

        this.menu = React.createRef();
        this.mapRef = React.createRef();
        this.appContainerRef = React.createRef();

        AppService.setAppComponent(this);

        ModalComponentService.setHandleComponentChangeFunction(this.handleCompoentChange);

        this.handleOpenPage = this.handleOpenPage.bind(this);
        this.handleClosePage = this.handleClosePage.bind(this);
        GronzePageService.init();

        this.statusRequestCallback = this.statusRequestCallback.bind(this);
    }

    /**
     * Screen focus event. Periodically re-check app version and authentication.
     */
    handleScreenFocus(): void {
        if (window.navigator.onLine) {
            //this.checkUpdates();
        }
    }

    componentDidMount(): void {
        window.addEventListener('focus', this.handleScreenFocus, false);
        SiteAuth.authenticateHandle = this.onAutorize;

        if (AppService.isNativeApp()) {
            AppLifecycleService.init();
            RateAppService.initAppActiveHandler();
            NetworkService.initAppActiveHandler();
            // Al abrir y al volver de segundo plano.
            AppLifecycleService.addAppActiveHandler(this.checkAppUpdate);
            this.checkAppUpdate();
        }

        if (AppService.isFirstTime()) {
            if (AppService.isNativeApp()) {
                RateAppService.registerActivity();
            }
            AppStorage.setOpenedBefore(true);
            ModalComponentService.openModalComponent(ModalComponentService.ModalComponentTypes.INITIAL_CARD);
        } else if (AppService.isNativeApp()) {
            PermissionsService.initAppActiveHandler();
            RateAppService.checkAndPrompt();
        }

        this.initApp();
    }

    componentWillUnmount(): void {
        window.removeEventListener('focus', this.handleScreenFocus, false)
        AppLifecycleService.removeAppActiveHandler(this.checkAppUpdate);
    }

    async initApp(): Promise<void> {
        this.logger().debug("Initializing App");
        MapService.mapLoaded = false;
        this.loadEnv();

        await NetworkService.init();

        try {
            if (!AppService.needsUserLogin()) {
                await SiteAuth.clientAuthenticate();
            } else if (!NetworkService.isOffline() && !AppService.isRedirecting() && await this.accessControl()) {
                this.loadMap();
            } else if (AppService.needsUserLogin() &&
                !this.state.authenticated &&
                !NetworkService.isOffline() &&
                !this.state.initialized) {
                this.setState({
                    isLoading: false,
                })
            } else {
                this.initPage();
            }
        } finally {
            // Cada rama ya ha dejado puesto lo suyo (autenticado o no), asi que
            // solo ahora puede aparecer el login sin que sea un parpadeo.
            this.setState({ startupResolved: true });
        }

    }

    // Periodically re-check authentication.
    //
    // La version ya no se mira aqui: en PWA la actualiza el service worker y
    // en nativo se pregunta al backend en cada arranque y vuelta a primer
    // plano. @see checkAppUpdate
    async checkUpdates(): Promise<void> {
        if (this.state.authenticated && this.state.initialized && SiteAuth.needsRefresh()) {
            // Recheck authentication, reload init page if fails.
            SiteAuth.refreshAuthentication().then((auth) => {
                if (!auth) this.initApp();
            });
        }
    }

    /**
     * Marca el boton de menu, y con el la entrada de actualizar del menu,
     * cuando el backend publica una version mayor que la instalada, y avisa
     * con un dialogo la primera vez que se ve cada version.
     *
     * Solo en app nativa: la PWA se actualiza sola con el service worker.
     * Corre al arrancar y en cada vuelta a primer plano, en segundo plano y
     * sin bloquear nada; si falla, simplemente no se marca.
     *
     * Se pregunta tambien con el aviso ya puesto, y no solo hasta encontrarlo:
     * el dialogo puede haberse quedado sin abrir por tener otra modal delante,
     * y asi lo vuelve a intentar. @see noticeAppUpdate
     */
    checkAppUpdate(): void {
        UpdateService.checkAppUpdate().then((update) => {
            if (!update) {
                // El aviso, una vez puesto, no se retira: una comprobacion
                // puede volver vacia por estar sin red, no porque se haya
                // despublicado la version.
                return;
            }

            this.logger().info('App update available', update.app_version);
            this.setState({ updateAvailable: true });
            this.noticeAppUpdate(update.app_version);
        });
    }

    /**
     * Abre el dialogo de version nueva, una sola vez por version publicada.
     *
     * Se salta si hay otra modal abierta —el slider del primer arranque, la
     * peticion de valoracion— y en ese caso no la da por avisada, asi que se
     * reintenta en la siguiente vuelta a primer plano o en el proximo
     * arranque.
     */
    noticeAppUpdate(version: string): void {
        if (!UpdateService.shouldNoticeUpdate(version) || ModalComponentService.modalComponentType) {
            return;
        }

        UpdateService.setUpdateNoticed(version);
        ModalComponentService.openModalComponent(
            ModalComponentService.ModalComponentTypes.UPDATE_APP_CARD
        );
    }

    /**
     * Get logger.
     */
    logger(): Logger {
        return log.getLogger('App');
    }

    /**
     * Gets map component.
     *
     */
    getMapComponent(): MapComponent | null {
        if (this.mapRef) {
            return this.mapRef.current;
        }
        return null;
    }

    /**
     * Change permission status handler fired from permissions service
     *
     * Parameters:
     * - value: permission status. One of geoPermStatus
     */
    changePermissionHandler(value: string): void {
        this.logger().debug('changePermissionHandler', value);

        this.geoPermissionsGranted = PermissionsService.geoPermissionGranted;

        // change state
        /*
        this.setState({
            mapVisible: PermissionsService.geoPermissionGranted,
            geoPermissionsGranted: PermissionsService.geoPermissionGranted,
        })
        */

        // on iOS we need to propagate the event even if the last state was prompt to handle the resume from settings
        const allowUpdate = AppService.isIos() || this.lastGeoState !== geoPermStatus.prompt;

        if (this.lastGeoState && allowUpdate && this.state.mapVisible && this.getMapComponent()) {
            MapService.changePermissionHandler(value);
        }

        this.lastGeoState = value;
    }

    /**
     * Check access control:
     * - If we are authenticated.
     * - Valid user is loaded.
     */
    async accessControl(): Promise<boolean> {
        return await SiteAuth.checkAuthentication() && await SiteAuth.requireValidUser();
    }

    // load environment vars
    loadEnv(): void {
        window.env = {
            base_url: import.meta.env.VITE_APP_BASE_URL,
        };
    }

    /**
     * Initial page when there's some error condition.
     */
    async initPage(): Promise<void> {
        // show error cards
        this.setState({
            authenticated: SiteAuth.isValidUser(),
            geoPermissionsGranted: PermissionsService.geoPermissionGranted,
            mapVisible: false,
            initialized: false,
            isLoading: false,
        })

    }

    /**
     * Load the city list, which feeds the city search.
     *
     * Runs once we are authenticated, as the fetch needs authorization data,
     * and in the background: the map and the URL deep link must neither wait
     * for it nor fail with it, so a failure only costs the city search until
     * the next start.
     */
    loadCityList(): void {
        CityService.checkAndInit().catch((error) => {
            this.logger().warn('City list not loaded, city search will not find results.', error);
        });
    }

    // the last step: load the map
    async loadMap(): Promise<void> {
        this.loadCityList();

        // show error cards
        this.setState({
            authenticated: true, // SiteAuth.isValidUser(),
            geoPermissionsGranted: PermissionsService.geoPermissionGranted,
            mapVisible: true,
            initialized: true,
            isLoading: false,
        })
    }

    /**
     * Menu handling functions
     */
    toggleMenu(): void {
        this.setState({
            menuVisible: !this.state.menuVisible,
        })
    }

    closeMenu(): void {
        if (this.state.menuVisible) {
            this.setState({
                menuVisible: false,
            })
        }
    }

    // Just some callbacks required by SwipeableDrawer (?)
    onMenuOpen = function (this: App) {
        log.debug("onMenuOpen");
        this.setState({
            menuVisible: true
        });
    };

    onMenuClose = function (this: App) {
        log.debug("onMenuClose");
        this.setState({
            menuVisible: false
        });
    };

    /**
     * Sends app to map view after login
     */
    onAutorize(): void {
        // Request and initialize Location permissions
        PermissionsService.initGeoPermissionStatus(this.changePermissionHandler);
        this.loadCityList();
        this.setState({
            authenticated: true,
            mapVisible: true,
        }, () => {
            // A deep link may have reached the map before there was any
            // authorization data, losing its card silently. Now that we are
            // authenticated, ask for it again.
            this.getMapComponent()?.showUrlFeature();
        })
    }

    onUnautorize(): void {
        SiteAuth.resetAuthentication();
        MapService.destroyMap();
        this.setState({
            authenticated: false,
            mapVisible: false,
        })
        AppStorage.reset();
        // remove authorization from inAppBrowser session
        UrlService.logout();
    }

    async handleOpenPage(component: ReactNode): Promise<void> {
        await this.getMapComponent()?.hideCurrentCard();
        this.setState({
            gronzePageComponent: component,
        })
    }

    handleClosePage(): void {
        this.setState({
            gronzePageComponent: null,
        })
    }

    /**
     * Close every overlay that may be open: modal, Gronze page, menu drawer and
     * map card.
     *
     * Centralized entry point used when a deep link brings the app to a specific
     * place and any previously open UI must be dismissed first.
     */
    closeAllOverlays(): void {
        // Modal (help/initial slider, languages, show-on-map, report error,
        // disclaimer, permissions, rate app...).
        ModalComponentService.openModalComponent(null);
        // Gronze page (terms of use, privacy, about/help, external pages) and
        // the menu drawer.
        this.setState({
            gronzePageComponent: null,
            menuVisible: false,
        });
        // Map detail card.
        this.getMapComponent()?.hideCurrentCard();
    }

    /**
     * Close the topmost overlay that may be open and report whether there was
     * one.
     *
     * Entry point for the Android back button, which dismisses one layer per
     * press instead of everything at once (see closeAllOverlays). Returns false
     * when nothing was left to close, so the caller can exit the app.
     */
    closeTopOverlay(): boolean {
        // Modal (help/initial slider, languages, show-on-map, report error,
        // disclaimer, permissions, rate app...).
        if (this.state.modalComponentType) {
            ModalComponentService.openModalComponent(null);
            return true;
        }
        // Gronze page (terms of use, privacy, about/help, external pages).
        if (this.state.gronzePageComponent) {
            this.handleClosePage();
            return true;
        }
        // Menu drawer.
        if (this.state.menuVisible) {
            this.closeMenu();
            return true;
        }
        // Map detail card.
        if (this.getMapComponent()?.isCardVisible()) {
            this.getMapComponent()?.hideCurrentCard();
            return true;
        }

        return false;
    }

    statusRequestCallback(isLoading: boolean): void {
        // console.log('isLoading ->', isLoading, new Error().stack); // Debug callback calls
        this.setState({ isLoading });
    }

    handleCompoentChange(): void {
        this.setState({
            modalComponentType: ModalComponentService.modalComponentType,
        })
    }

    render() {
        let mapComponent;
        let appMenu;
        let classActiveMenu = '';
        let modalCard;

        if (this.state.mapVisible) {
            mapComponent = <MapComponent
                ref={this.mapRef}
                allowDetail={!this.state.menuVisible}
            />
            appMenu = <Menu
                ref={this.menu}
                unautorize={this.onUnautorize}
                updateAvailable={this.state.updateAvailable}
            />
            classActiveMenu = 'active';
            if (this.state.modalComponentType) {
                modalCard = ModalComponentService.getModalComponent();
            } else if (GeoService.isUsingConfigInitialLocation() && this.state.initialToastShown) {
                setTimeout(() => {
                    this.setState({
                        initialToastShown: false,
                    });
                    LogManager.createInfo(i18n.t('app.initialToast'), true);
                }, 1000);
            }
        }
        let blockedApCard;
        let showingInitSession = false;

        // Sin login, no autenticado solo significa que falta el token anónimo,
        // y SiteAuth lo reintenta: la pantalla de login no tiene sentido (#673).
        if (AppService.needsUserLogin() &&
            !this.state.authenticated &&
            !NetworkService.isOffline() &&
            !this.state.initialized &&
            this.state.startupResolved
        ) {
            // init session error
            blockedApCard = <InitSessionScreen />
            showingInitSession = true;
        } else if (this.state.geoPermissionsFail && this.state.initialized) {
            // geolocation permission not granted
            blockedApCard = <GeoPermissionFailCard />
        }

        let gronzePage;
        if (this.state.gronzePageComponent !== null) {
            gronzePage = GronzePageService.getPage(this.state.gronzePageComponent);
        }

        let isLoading;
        if (this.state.isLoading) {
            isLoading = <CircularProgress
                size={60}
                thickness={6}
                className="circularProgress"
            />;
        }

        return (
            // themeprovider to get material customization styles
            <div className='appComponent' onClick={this.closeMenu} ref={this.appContainerRef}>
                <AppUrlListener></AppUrlListener>
                <CssBaseline />
                {mapComponent}
                {blockedApCard}
                {
                    // The card-link init session screen shows its own app icon,
                    // so the header logo (and its title) are left out there.
                    !(showingInitSession && UrlParamsService.isCardLink()) &&
                    <header className={!showingInitSession && this.state.updateAvailable ? 'update' : undefined}>
                        <img className={showingInitSession ? 'logo' : ('logo ' + classActiveMenu)}
                            src={showingInitSession ? logoImagePlain : logoImage} alt="Logo Gronze"
                            onClick={showingInitSession ? undefined : this.toggleMenu} />
                    </header>
                }
                {gronzePage}
                <SwipeableDrawer
                    open={this.state.menuVisible}
                    onOpen={this.onMenuOpen}
                    onClose={this.onMenuClose}
                    disableSwipeToOpen={false}
                    hysteresis={0.5}
                >
                    {appMenu}
                </SwipeableDrawer>

                <SnackComponent />

                <ScreenLogCard />

                {isLoading}

                <div className="modal-container">{modalCard}</div>
            </div>
        );
    }

}

export default App;
